import FilterModal, {
  FilterGroup,
} from 'components/Filters/components/FilterModal'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import {
  ContactCountRangeFilter,
  NoteCountRangeFilter,
  RangeFilter,
} from 'components/Filters/fields/RangeFilter'
import {
  SelectFilter,
  SelectFilterOption,
} from 'components/Filters/fields/SelectFilter'
import { useCategorySubcategoryOptions } from 'hooks/useCategorySubcategoryFilterOptions'
import apiService from 'services/api'

import { FilterIdentifier } from 'models/saved_view'
import { filterStoreRepo } from '../../Filters/FilterStore'
import { createFilterColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import { featureFlagService } from 'utils/featureFlagService'
import { useMemo } from 'react'
import { useGetProductsCatalog } from 'services/products_api'

export function DealsFilterset(props: {
  filterIdentifier: FilterIdentifier
  hideCampaignsFilter?: boolean
}) {
  const api = apiService()
  const { data: userOptions } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: true,
  })
  const { data: stageOptions } = api.useGetSalesStageOptions(true)
  const { data: tagsOptions } = api.useGetTagsOptions(true)

  // Use new products catalog API instead of legacy API
  const { data: catalogResponse } = useGetProductsCatalog({
    params: { page_size: 1000 }, // Get all products
    useNewCatalog: true,
  })

  const { data: productVariantsOptions } =
    api.useGetProductVariantsOptions(true)
  const { data: distributorOptions } =
    api.useGetCompanyDistributorsOptions(true)
  const { data: gpoOptions } = api.useGetCompanyGPOsOptions(true)
  const { data: contactTagsOptions } = api.useGetTagsOptions(true, 'contacts')
  const dealsFilterStore = filterStoreRepo.getStore(props.filterIdentifier)
  const { categoriesOptions, subCategoriesOptions } =
    useCategorySubcategoryOptions({ filterStore: dealsFilterStore })
  const { data: campaignOptions } = api.useGetCampaignsOptions()
  const { enableEmailIntegration, enabledReportDeals } = featureFlagService()

  const reportsDisabledTooltip = !enabledReportDeals
    ? 'Revenue calculations are currently down for maintenance.'
    : undefined

  // Convert product catalog response to SelectFilter options
  const productsOptions: SelectFilterOption[] = useMemo(() => {
    if (!catalogResponse?.results) return []
    return catalogResponse.results.map((product) => ({
      label: product.name,
      value: String(product.id),
    }))
  }, [catalogResponse])

  const dealFilterHelper = createFilterColumnHelper({ columns: DEAL_COLUMNS })

  return (
    <FilterModal store={dealsFilterStore} identifier={props.filterIdentifier}>
      <FilterGroup title={'Opportunity Details'}>
        <RangeFilter
          {...dealFilterHelper.retrieveProps('company_size')}
          store={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('category')}
          options={categoriesOptions}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('sub_category')}
          options={subCategoriesOptions}
          filterStore={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('estimated_meals_per_day')}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('operational_schools')}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('doors_count')}
          store={dealsFilterStore}
        />
      </FilterGroup>
      <FilterGroup title="Account">
        <SelectFilter
          {...dealFilterHelper.retrieveProps('product')}
          options={productsOptions}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('product_variant')}
          options={productVariantsOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('sales_stage')}
          options={stageOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('account_owner')}
          options={userOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('taglist')}
          options={tagsOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('contact_tags')}
          options={contactTagsOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('distributors')}
          options={distributorOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('gpos')}
          options={gpoOptions as any}
          filterStore={dealsFilterStore}
        />
        <NoteCountRangeFilter store={dealsFilterStore} />
        <ContactCountRangeFilter store={dealsFilterStore} />
        {!props.hideCampaignsFilter && (
          <SelectFilter
            {...dealFilterHelper.retrieveProps('origin_campaign')}
            filterKey="origin_campaign"
            options={campaignOptions as any}
            filterStore={dealsFilterStore}
          />
        )}
      </FilterGroup>
      <FilterGroup title="Predicted Values">
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_volume_predicted')}
          decimalScale={1}
          store={dealsFilterStore}
          disabled={!enabledReportDeals}
          disabledTooltipText={reportsDisabledTooltip}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_revenue_predicted')}
          store={dealsFilterStore}
          disabled={!enabledReportDeals}
          disabledTooltipText={reportsDisabledTooltip}
        />
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('expected_days_to_close')}
          filterStore={dealsFilterStore}
        />
      </FilterGroup>
      <FilterGroup title="Overrides">
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_volume_override')}
          decimalScale={1}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_revenue_override')}
          store={dealsFilterStore}
        />
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('close_date_override')}
          filterStore={dealsFilterStore}
        />
      </FilterGroup>
      <FilterGroup title="User Activity">
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('modified')}
          filterStore={dealsFilterStore}
        />
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('created')}
          filterStore={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('last_datetime_stage_changed')}
          decimalScale={1}
          store={dealsFilterStore}
          minAllowNegative={false}
          maxAllowNegative={false}
        />
        {enableEmailIntegration && (
          <>
            <RangeFilter
              {...dealFilterHelper.retrieveProps('days_since_reply')}
              decimalScale={0}
              store={dealsFilterStore}
              minAllowNegative={false}
              maxAllowNegative={false}
            />
            <RangeFilter
              {...dealFilterHelper.retrieveProps('days_since_sent')}
              decimalScale={0}
              store={dealsFilterStore}
              minAllowNegative={false}
              maxAllowNegative={false}
            />
          </>
        )}
        <RangeFilter
          {...dealFilterHelper.retrieveProps('days_since_last_modified')}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('days_since_deal_last_modified')}
          store={dealsFilterStore}
        />
      </FilterGroup>
    </FilterModal>
  )
}
